import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AccountType } from "@/api/rest/models/account";
import { AdminBuilding, AdminUser, CreateAccountRequest } from "@/api/rest/models/admin";
import {
  addBuildingToAccount,
  addUserToAccount,
  createAccount,
  deleteAccount,
  getAccountWithUsers,
  listAccounts,
  listBuildings,
  listUsers,
  removeBuildingFromAccount,
  removeUserFromAccount,
} from "@/api/rest/services/adminService";
import ConfirmationDialog from "@/components/common/ConfirmationDialog";
import Spinner from "@/components/common/Spinner";

import CollapsibleSection from "./CollapsibleSection";

type SortColumn = "id" | "name" | "type" | "registryCode";
type SortDirection = "asc" | "desc";

const SORT_COLUMNS: SortColumn[] = ["id", "name", "type", "registryCode"];

const isValidSortColumn = (value: string | null): value is SortColumn => {
  return value !== null && SORT_COLUMNS.includes(value as SortColumn);
};

export default function AccountsTab({
  selectedAccountId,
  onSelectedAccountIdChange,
  onSelectUser,
  onSelectBuilding,
}: {
  selectedAccountId: number | null;
  onSelectedAccountIdChange: (accountId: number | null) => void;
  onSelectUser: (userId: number) => void;
  onSelectBuilding?: (buildingId: number) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const search = searchParams.get("accountSearch") ?? "";
  const setSearch = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("accountSearch", value);
    } else {
      newParams.delete("accountSearch");
    }
    setSearchParams(newParams, { replace: true });
  };
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddBuildingModal, setShowAddBuildingModal] = useState(false);
  const [pendingRemoveUser, setPendingRemoveUser] = useState<{ userId: number; email: string } | null>(null);
  const [pendingRemoveBuilding, setPendingRemoveBuilding] = useState<{ buildingId: number; address: string } | null>(
    null,
  );
  const [pendingDeleteAccount, setPendingDeleteAccount] = useState<{ accountId: number; name: string } | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  const sortColumnParam = searchParams.get("accountSort");
  const sortColumn: SortColumn = isValidSortColumn(sortColumnParam) ? sortColumnParam : "id";
  const sortDirection: SortDirection = searchParams.get("accountDir") === "desc" ? "desc" : "asc";

  const updateSortParams = (column: SortColumn, direction: SortDirection) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("accountSort", column);
    newParams.set("accountDir", direction);
    setSearchParams(newParams, { replace: true });
  };

  const accountsQuery = useInfiniteQuery({
    queryKey: ["admin-accounts", search, sortColumn, sortDirection],
    queryFn: ({ pageParam = 0 }) => listAccounts(search || undefined, pageParam, 20, sortColumn, sortDirection),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const result = lastPage.result;
      if (!result || result.page >= result.totalPages - 1) {
        return undefined;
      }
      return result.page + 1;
    },
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && accountsQuery.hasNextPage && !accountsQuery.isFetchingNextPage) {
          accountsQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsQuery.hasNextPage, accountsQuery.isFetchingNextPage, accountsQuery.fetchNextPage]);

  const accountDetailsQuery = useQuery({
    queryKey: ["admin-account", selectedAccountId],
    queryFn: () => getAccountWithUsers(selectedAccountId!),
    enabled: selectedAccountId !== null,
  });

  const createMutation = useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-accounts"] });
      setShowCreateModal(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-accounts"] });
      onSelectedAccountIdChange(null);
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: ({ accountId, userId }: { accountId: number; userId: number }) =>
      removeUserFromAccount(accountId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-account", selectedAccountId] });
    },
  });

  const addUserMutation = useMutation({
    mutationFn: ({ accountId, userId }: { accountId: number; userId: number }) => addUserToAccount(accountId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-account", selectedAccountId] });
      setShowAddUserModal(false);
    },
  });

  const addBuildingMutation = useMutation({
    mutationFn: ({ accountId, buildingId }: { accountId: number; buildingId: number }) =>
      addBuildingToAccount(accountId, buildingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-account", selectedAccountId] });
      setShowAddBuildingModal(false);
    },
  });

  const removeBuildingMutation = useMutation({
    mutationFn: ({ accountId, buildingId }: { accountId: number; buildingId: number }) =>
      removeBuildingFromAccount(accountId, buildingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-account", selectedAccountId] });
    },
  });

  const loadedAccounts = accountsQuery.data?.pages.flatMap((page) => page.result?.items ?? []) ?? [];
  const accountDetails = accountDetailsQuery.data?.result;

  // If selected account is not in the loaded list, prepend it from the details query
  const selectedAccountInList = selectedAccountId ? loadedAccounts.some((a) => a.id === selectedAccountId) : true;
  const accounts =
    !selectedAccountInList && accountDetails
      ? [accountDetails.account, ...loadedAccounts.filter((a) => a.id !== accountDetails.account.id)]
      : loadedAccounts;

  // Scroll to selected row when data is loaded and row is available
  useEffect(() => {
    if (selectedAccountId && (accounts.length > 0 || accountDetails)) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        if (selectedRowRef.current) {
          selectedRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }
  }, [selectedAccountId, accounts.length, accountDetails]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      updateSortParams(column, sortDirection === "asc" ? "desc" : "asc");
    } else {
      updateSortParams(column, "asc");
    }
  };

  const SortIndicator = ({ column }: { column: SortColumn }) => (
    <span className="ml-1">{sortColumn === column ? (sortDirection === "asc" ? "▲" : "▼") : ""}</span>
  );

  const adminBuildings =
    accountDetails?.developments
      .flatMap((dev) => dev.buildings.filter((b) => b.isDirectAdmin))
      .map((b) => b.building) ?? [];

  const renderExpandedRow = (accountId: number) => {
    if (selectedAccountId !== accountId) {
      return null;
    }

    return (
      <tr key={`details-${accountId}`}>
        <td colSpan={4} className="p-0 border-b">
          <div className="bg-gray-50 border-t">
            {accountDetailsQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-8 h-8" />
              </div>
            ) : accountDetails ? (
              <div>
                {/* Header */}
                <div className="flex items-start justify-between p-4 pb-3 border-b border-gray-200">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{accountDetails.account.name}</h2>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                        {accountDetails.account.type}
                      </span>
                      {accountDetails.account.registryCode && (
                        <>
                          <span>•</span>
                          <span>Registry: {accountDetails.account.registryCode}</span>
                        </>
                      )}
                      <span>•</span>
                      <span>ID: {accountDetails.account.id}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onSelectedAccountIdChange(null)}
                    className="btn-custom p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Collapsible Sections */}
                <div className="px-4">
                  {/* Users Section */}
                  <CollapsibleSection
                    title="Users"
                    count={accountDetails.users.length}
                    action={
                      <button
                        onClick={() => setShowAddUserModal(true)}
                        className="btn-custom px-2 py-1 text-xs font-medium text-dobu-orange hover:bg-orange-50 rounded transition-colors"
                      >
                        + Add
                      </button>
                    }
                  >
                    {accountDetails.users.length === 0 ? (
                      <p className="text-gray-500 text-sm">No users in this account</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {accountDetails.users.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <div
                              onClick={() => onSelectUser(user.id)}
                              className="cursor-pointer hover:text-dobu-orange transition-colors min-w-0 flex-1"
                            >
                              <div className="font-medium text-sm truncate">{user.email}</div>
                              <div className="text-xs text-gray-500 truncate">{user.name}</div>
                            </div>
                            <button
                              onClick={() => setPendingRemoveUser({ userId: user.id, email: user.email })}
                              disabled={removeUserMutation.isPending}
                              className="btn-custom ml-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Remove user"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  {/* Buildings (Admin) Section */}
                  <CollapsibleSection
                    title="Buildings - Admin"
                    count={adminBuildings.length}
                    action={
                      <button
                        onClick={() => setShowAddBuildingModal(true)}
                        className="btn-custom px-2 py-1 text-xs font-medium text-dobu-orange hover:bg-orange-50 rounded transition-colors"
                      >
                        + Add
                      </button>
                    }
                  >
                    {adminBuildings.length === 0 ? (
                      <p className="text-gray-500 text-sm">No buildings where this account is admin</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {adminBuildings.map((building) => (
                          <div
                            key={building.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <div
                              onClick={() => onSelectBuilding?.(building.id)}
                              className="cursor-pointer hover:text-dobu-orange transition-colors min-w-0 flex-1"
                            >
                              <div className="font-medium text-sm truncate">
                                {building.street}, {building.city}
                              </div>
                              <div className="text-xs text-gray-500">ID: {building.id}</div>
                            </div>
                            <button
                              onClick={() =>
                                setPendingRemoveBuilding({
                                  buildingId: building.id,
                                  address: `${building.street}, ${building.city}`,
                                })
                              }
                              disabled={removeBuildingMutation.isPending}
                              className="btn-custom ml-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Remove building"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  {/* Households Section */}
                  <CollapsibleSection
                    title="Households"
                    count={accountDetails.developments.length > 0 ? accountDetails.developments.length : undefined}
                    defaultOpen={accountDetails.developments.length > 0}
                  >
                    {accountDetails.developments.length === 0 ? (
                      <p className="text-gray-500 text-sm">No households</p>
                    ) : (
                      <div className="space-y-3">
                        {accountDetails.developments.map((dev, idx) => (
                          <div
                            key={dev.development?.id ?? `standalone-${idx}`}
                            className="p-3 bg-white rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm text-gray-900">
                                {dev.development?.name ?? "Standalone Buildings"}
                              </span>
                              {dev.isDevelopmentAdmin && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                                  Dev. Admin
                                </span>
                              )}
                            </div>
                            <div className="space-y-1.5 text-sm">
                              {dev.buildings.map((building) => (
                                <div key={building.building.id} className="flex items-start gap-2">
                                  <span className="text-gray-400">•</span>
                                  <div className="flex-1 min-w-0">
                                    <span
                                      className="cursor-pointer hover:text-dobu-orange transition-colors"
                                      onClick={() => onSelectBuilding?.(building.building.id)}
                                    >
                                      {building.building.street}, {building.building.city}
                                    </span>
                                    {building.isDirectAdmin && !dev.isDevelopmentAdmin && (
                                      <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                                        Admin
                                      </span>
                                    )}
                                    <span className="text-gray-400 ml-2">
                                      {building.households.length > 0
                                        ? building.households.map((h, idx) => (
                                            <span key={h.id}>
                                              {idx > 0 && ", "}
                                              <span
                                                className="cursor-pointer hover:text-dobu-orange transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(`/household/${h.id}`);
                                                }}
                                              >
                                                {h.title}
                                              </span>
                                            </span>
                                          ))
                                        : "No households"}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {dev.buildings.length === 0 && <span className="text-gray-400 italic">No buildings</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  {/* Danger Zone */}
                  <CollapsibleSection title="Danger Zone" defaultOpen={false}>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-red-800">Delete Account</p>
                          <p className="text-xs text-red-600">Permanently remove this account and all its data</p>
                        </div>
                        <button
                          onClick={() =>
                            setPendingDeleteAccount({
                              accountId: accountDetails.account.id,
                              name: accountDetails.account.name,
                            })
                          }
                          disabled={deleteMutation.isPending}
                          className="btn-custom px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {deleteMutation.isPending ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  </CollapsibleSection>
                </div>
              </div>
            ) : (
              <div className="text-red-500 p-4">Failed to load account details</div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Search accounts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dobu-orange"
        />
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-dobu-orange text-white rounded-lg hover:bg-dobu-orange/90"
        >
          Create Account
        </button>
      </div>

      {accountsQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="w-8 h-8" />
        </div>
      ) : accountsQuery.isError ? (
        <div className="text-red-500 py-4">Failed to load accounts</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("id")}
                  >
                    ID
                    <SortIndicator column="id" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("name")}
                  >
                    Name
                    <SortIndicator column="name" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("type")}
                  >
                    Type
                    <SortIndicator column="type" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("registryCode")}
                  >
                    Registry Code
                    <SortIndicator column="registryCode" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <React.Fragment key={account.id}>
                    <tr
                      ref={selectedAccountId === account.id ? selectedRowRef : null}
                      className={`border-b cursor-pointer hover:bg-gray-50 ${
                        selectedAccountId === account.id ? "bg-blue-50" : ""
                      }`}
                      onClick={() => onSelectedAccountIdChange(selectedAccountId === account.id ? null : account.id)}
                    >
                      <td className="p-3">{account.id}</td>
                      <td className="p-3">{account.name}</td>
                      <td className="p-3">{account.type}</td>
                      <td className="p-3">{account.registryCode ?? "-"}</td>
                    </tr>
                    {renderExpandedRow(account.id)}
                  </React.Fragment>
                ))}
                {accounts.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-4 text-center text-gray-500">
                      No accounts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div ref={loadMoreRef} className="flex justify-center py-4">
            {accountsQuery.isFetchingNextPage && <Spinner className="w-6 h-6" />}
          </div>
        </>
      )}

      {showCreateModal && (
        <CreateAccountModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}

      {showAddUserModal && selectedAccountId && (
        <AddUserModal
          existingUserIds={accountDetails?.users.map((u) => u.id) ?? []}
          onClose={() => setShowAddUserModal(false)}
          onAdd={(userId) => addUserMutation.mutate({ accountId: selectedAccountId, userId })}
          isLoading={addUserMutation.isPending}
        />
      )}

      {showAddBuildingModal && selectedAccountId && (
        <AddBuildingModal
          existingBuildingIds={
            accountDetails?.developments.flatMap((d) =>
              d.buildings.filter((b) => b.isDirectAdmin).map((b) => b.building.id),
            ) ?? []
          }
          onClose={() => setShowAddBuildingModal(false)}
          onAdd={(buildingId) => addBuildingMutation.mutate({ accountId: selectedAccountId, buildingId })}
          isLoading={addBuildingMutation.isPending}
        />
      )}

      <ConfirmationDialog
        isOpen={pendingRemoveUser !== null}
        onCancel={() => setPendingRemoveUser(null)}
        onConfirm={() => {
          if (pendingRemoveUser && selectedAccountId) {
            removeUserMutation.mutate({
              accountId: selectedAccountId,
              userId: pendingRemoveUser.userId,
            });
            setPendingRemoveUser(null);
          }
        }}
        title="Remove User"
        message={`Are you sure you want to remove "${pendingRemoveUser?.email}" from this account?`}
        confirmText="Remove"
        cancelText="Cancel"
      />

      <ConfirmationDialog
        isOpen={pendingRemoveBuilding !== null}
        onCancel={() => setPendingRemoveBuilding(null)}
        onConfirm={() => {
          if (pendingRemoveBuilding && selectedAccountId) {
            removeBuildingMutation.mutate({
              accountId: selectedAccountId,
              buildingId: pendingRemoveBuilding.buildingId,
            });
            setPendingRemoveBuilding(null);
          }
        }}
        title="Remove Building"
        message={`Are you sure you want to remove "${pendingRemoveBuilding?.address}" from this account?`}
        confirmText="Remove"
        cancelText="Cancel"
      />

      <ConfirmationDialog
        isOpen={pendingDeleteAccount !== null}
        onCancel={() => setPendingDeleteAccount(null)}
        onConfirm={() => {
          if (pendingDeleteAccount) {
            deleteMutation.mutate(pendingDeleteAccount.accountId);
            setPendingDeleteAccount(null);
          }
        }}
        title="Delete Account"
        message={`Are you sure you want to delete the account "${pendingDeleteAccount?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}

function CreateAccountModal({
  onClose,
  onCreate,
  isLoading,
}: {
  onClose: () => void;
  onCreate: (data: CreateAccountRequest) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState<AccountType>(AccountType.INDIVIDUAL);
  const [registryCode, setRegistryCode] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      name,
      type,
      registryCode: type === AccountType.ORGANIZATION ? registryCode : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Create Account</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Account Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as AccountType)}
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
            >
              <option value={AccountType.INDIVIDUAL}>Individual</option>
              <option value={AccountType.ORGANIZATION}>Organization</option>
            </select>
          </div>
          {type === AccountType.ORGANIZATION && (
            <div>
              <label className="block text-sm font-medium mb-1">Registry Code</label>
              <input
                type="text"
                value={registryCode}
                onChange={(e) => setRegistryCode(e.target.value)}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
              />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-100">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name}
              className="px-4 py-2 bg-dobu-orange text-white rounded hover:bg-dobu-orange/90 disabled:opacity-50"
            >
              {isLoading ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddUserModal({
  existingUserIds,
  onClose,
  onAdd,
  isLoading,
}: {
  existingUserIds: number[];
  onClose: () => void;
  onAdd: (userId: number) => void;
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  const usersQuery = useQuery({
    queryKey: ["admin-users-for-add", search],
    queryFn: () => listUsers(search || undefined, 0, 10),
    enabled: search.length >= 2,
  });

  const availableUsers = (usersQuery.data?.result?.items ?? []).filter((u) => !existingUserIds.includes(u.id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Add User to Account</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Search User</label>
            <input
              type="text"
              placeholder="Type at least 2 characters..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedUser(null);
              }}
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
            />
          </div>

          {usersQuery.isLoading && (
            <div className="flex justify-center py-2">
              <Spinner className="w-6 h-6" />
            </div>
          )}

          {availableUsers.length > 0 && (
            <ul className="max-h-48 overflow-y-auto border rounded">
              {availableUsers.map((user) => (
                <li
                  key={user.id}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedUser?.id === user.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedUser(user)}
                >
                  <div className="font-medium">{user.email}</div>
                  <div className="text-sm text-gray-500">{user.name}</div>
                </li>
              ))}
            </ul>
          )}

          {search.length >= 2 && !usersQuery.isLoading && availableUsers.length === 0 && (
            <div className="text-gray-500 text-center py-2">No users found</div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-100">
              Cancel
            </button>
            <button
              onClick={() => selectedUser && onAdd(selectedUser.id)}
              disabled={isLoading || !selectedUser}
              className="px-4 py-2 bg-dobu-orange text-white rounded hover:bg-dobu-orange/90 disabled:opacity-50"
            >
              {isLoading ? "Adding..." : "Add User"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddBuildingModal({
  existingBuildingIds,
  onClose,
  onAdd,
  isLoading,
}: {
  existingBuildingIds: number[];
  onClose: () => void;
  onAdd: (buildingId: number) => void;
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState<AdminBuilding | null>(null);

  const buildingsQuery = useQuery({
    queryKey: ["admin-buildings-for-add", search],
    queryFn: () => listBuildings(search || undefined, 0, 10),
    enabled: search.length >= 2,
  });

  const availableBuildings = (buildingsQuery.data?.result?.items ?? []).filter(
    (b) => !existingBuildingIds.includes(b.id),
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Add Building to Account</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Search Building</label>
            <input
              type="text"
              placeholder="Type at least 2 characters..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedBuilding(null);
              }}
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
            />
          </div>

          {buildingsQuery.isLoading && (
            <div className="flex justify-center py-2">
              <Spinner className="w-6 h-6" />
            </div>
          )}

          {availableBuildings.length > 0 && (
            <ul className="max-h-48 overflow-y-auto border rounded">
              {availableBuildings.map((building) => (
                <li
                  key={building.id}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedBuilding?.id === building.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedBuilding(building)}
                >
                  <div className="font-medium">
                    {building.street}, {building.city}
                  </div>
                  <div className="text-sm text-gray-500">
                    {building.postcode} - ID: {building.id}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {search.length >= 2 && !buildingsQuery.isLoading && availableBuildings.length === 0 && (
            <div className="text-gray-500 text-center py-2">No buildings found</div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-100">
              Cancel
            </button>
            <button
              onClick={() => selectedBuilding && onAdd(selectedBuilding.id)}
              disabled={isLoading || !selectedBuilding}
              className="px-4 py-2 bg-dobu-orange text-white rounded hover:bg-dobu-orange/90 disabled:opacity-50"
            >
              {isLoading ? "Adding..." : "Add Building"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
